import React, { useState } from "react";
import { useUploadFile } from "../../../components/upload_file_card.jsx";
import { AZURE_BLOB_CONTAINER_CERTIFICATES } from "../../../../../constants.jsx";
import { CERTIFICATE_SYSCODE } from "../../../../constants/certificate_syscode.jsx";
import { CERTIFICATE_PROFICIENCY_SYSCODE } from "../../../../constants/certificate_proficiency_syscode.jsx";
import { DEVELOPER_CERTIFICATION_STATUS } from "../../../../constants/developer_certification_status.jsx";
import beveatApiResourceOwner from "../../../../api/resource_owner_beveat_api.js";
import { commonConfigs, urls } from "../../../../../config.js";

const getCertificateTypeMapping = (syscode) => {
  const mapping = {
    // Sandbox
    [CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Web]: { type: "nets-qr-web", environment: "sandbox" },
    [CERTIFICATE_SYSCODE.SANDBOX_NETS_QR_Mobile]: { type: "nets-qr-mobile", environment: "sandbox" },
    [CERTIFICATE_SYSCODE.SANDBOX_eNETS_Web]: { type: "enets-web", environment: "sandbox" },
    
    // UAT
    [CERTIFICATE_SYSCODE.UAT_NETS_Click]: { type: "nets-click", environment: "uat" },
    [CERTIFICATE_SYSCODE.UAT_NETS_QR_Web]: { type: "nets-qr-web", environment: "uat" },
    [CERTIFICATE_SYSCODE.UAT_NETS_QR_Mobile]: { type: "nets-qr-mobile", environment: "uat" },
    [CERTIFICATE_SYSCODE.UAT_eNETS_Web]: { type: "enets-web", environment: "uat" },
    [CERTIFICATE_SYSCODE.UAT_eNETS_Mobile]: { type: "enets-mobile", environment: "uat" },
    
    // Go Live
    [CERTIFICATE_SYSCODE.GO_LIVE_NETS_Click]: { type: "nets-click", environment: "go-live" },
    [CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Web]: { type: "nets-qr-web", environment: "go-live" },
    [CERTIFICATE_SYSCODE.GO_LIVE_NETS_QR_Mobile]: { type: "nets-qr-mobile", environment: "go-live" },
    [CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Web]: { type: "enets-web", environment: "go-live" },
    [CERTIFICATE_SYSCODE.GO_LIVE_eNETS_Mobile]: { type: "enets-mobile", environment: "go-live" }
  };
  return mapping[parseInt(syscode)] || { type: "unknown", environment: "unknown" };
};

const getTrainingProviderMapping = (trainingProviderName) => {
  const providerName = (trainingProviderName || "").toLowerCase();
  
  if (providerName.includes("singapore polytechnic")) return "sp";
  if (providerName.includes("nanyang polytechnic")) return "nyp";
  if (providerName.includes("ngee ann polytechnic")) return "np";
  if (providerName.includes("republic polytechnic")) return "rp";
  if (providerName.includes("temasek polytechnic")) return "tp";
  if (providerName.includes("singapore institute of technology")) return "sit";
  
  return "unknown";
};

const getProficiencyMapping = (proficiencyCode) => {
  const mapping = {
    [CERTIFICATE_PROFICIENCY_SYSCODE.Completion]: "coc",
    [CERTIFICATE_PROFICIENCY_SYSCODE.Achievement]: "coa"
  };
  return mapping[parseInt(proficiencyCode)] || "unknown";
};

const UploadCertificateModal = ({
  isOpen,
  uploadingCertificate,
  handleCloseModal,
  onUploadSuccess,
}) => {
  const [file, fileCard, uploadFile, resetFile] = useUploadFile({ 
    container: AZURE_BLOB_CONTAINER_CERTIFICATES,
    acceptedFileTypes: "pdf"
  });
  
  const [isUploading, setIsUploading] = useState(false);

  const updateCertificateWithUploadedFile = async (developerCertificateId, developerEmail, certificateUrl) => {
    try {
      const requestBody = {
        developer_email: developerEmail,
        developer_certificate_id: developerCertificateId,
        developer_certificate_img_url: certificateUrl,
        developer_certification_status: DEVELOPER_CERTIFICATION_STATUS.Released
      };

      const updateUrl = urls.resourceOwnerUrl + commonConfigs.apiUrls.updateCertificate();
      
      const response = await beveatApiResourceOwner.put(updateUrl, requestBody, {
        headers: commonConfigs.resourceOwnerApiHeader,
      });

      return response.data?.status === "success";
    } catch (error) {
      console.error("Error updating certificate:", error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file to upload");
      return;
    }

    if (!uploadingCertificate) {
      alert("No certificate selected for upload");
      return;
    }

    setIsUploading(true);

    try {
      const originalData = uploadingCertificate._original || uploadingCertificate;
      const { certificate_syscode, training_provider_name, certificate_proficiency_syscode } = originalData;
      
      // Get mapping data
      const { type, environment } = getCertificateTypeMapping(certificate_syscode);
      const trainingProvider = getTrainingProviderMapping(training_provider_name);
      const proficiency = getProficiencyMapping(certificate_proficiency_syscode);

      // Create blob path: released/{type}/{environment}/{provider}/{proficiency}/filename
      const blobPath = `released/${type}/${environment}/${trainingProvider}/${proficiency}/${file.name}`;
      
      console.log('Certificate upload path:', blobPath);
      
      // Upload file
      const certificateUrl = await uploadFile(blobPath);
      console.log('File uploaded:', certificateUrl);
      
      if (certificateUrl) {
        const developerCertificateId = originalData.developer_certificate_id;
        const developerEmail = originalData.developer_email || uploadingCertificate.email;

        if (!developerCertificateId || !developerEmail) {
          throw new Error("Missing required certificate data");
        }

        const updateSuccess = await updateCertificateWithUploadedFile(
          developerCertificateId, 
          developerEmail,
          certificateUrl
        );

        if (updateSuccess) {
          alert("Certificate Released");
          handleClose();
          if (onUploadSuccess) {
            onUploadSuccess(uploadingCertificate);
          }
        } else {
          alert("File uploaded successfully, but failed to update certificate status.");
        }
      }
    } catch (error) {
      console.error("Upload/Update error:", error);
      alert(error.message || "An error occurred during upload");
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    resetFile(); // Clear the file state
    handleCloseModal();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">Upload Certificate</h2>
        </div>
        
        <div className="px-6 py-6">
          {uploadingCertificate && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Certificate Details</h3>
              <p className="text-sm text-gray-700"><strong>Name:</strong> {uploadingCertificate.name}</p>
              <p className="text-sm text-gray-700"><strong>Coursework:</strong> {uploadingCertificate.coursework}</p>
              <p className="text-sm text-gray-700"><strong>Environment:</strong> {uploadingCertificate.environment}</p>
              <p className="text-sm text-gray-700"><strong>Training Provider:</strong> {uploadingCertificate._original?.training_provider_name || uploadingCertificate.training_provider}</p>
            </div>
          )}

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Select Certificate File (PDF only)
            </label>
            {fileCard}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 transition-colors duration-200 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!file || isUploading}
            className={`px-6 py-2 rounded-md focus:outline-none focus:ring-2 transition-colors duration-200 ${
              file && !isUploading
                ? "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            {isUploading ? "Uploading & Releasing..." : "Upload & Release Certificate"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadCertificateModal;